// Month grid for picking an appointment day.
//
// Reuses the portal's existing `pp-medcal-*` styles (see MedsHistory.js and
// css/PatientPortal.css) so it looks native without new CSS.
//
// Three states, deliberately distinguishable:
//   bookable  — has a free slot; full opacity, dot marker
//   full      — published but every slot taken; dimmed, × marker, STILL clickable
//               so the patient can open it and see the times struck through
//   closed    — nothing published; faint and inert
// Collapsing "full" into "closed" would tell a patient the clinic is shut on a
// day it is simply busy.
import React, { useMemo } from "react";

const DOW = ["S", "M", "T", "W", "T", "F", "S"];

const iso = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** Leading blanks + one cell per day, so the 1st lands under its weekday. */
export function monthCells(year, month) {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: first }, () => null);
  for (let d = 1; d <= days; d += 1) {
    cells.push({ day: d, date: iso(year, month, d) });
  }
  return cells;
}

export default function AppointmentCalendar({
  monthDate,
  onMonthChange,
  slotsByDate,
  statusByDate,
  selectedDate,
  onSelectDate,
  minDate,
}) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const cells = useMemo(() => monthCells(year, month), [year, month]);
  const monthLabel = monthDate.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="pp-medcal">
      <div className="pp-medcal-head">
        <button
          type="button"
          className="pp-medcal-nav"
          aria-label="Previous month"
          onClick={() => onMonthChange(new Date(year, month - 1, 1))}
        >
          ‹
        </button>
        <div className="pp-medcal-month">{monthLabel}</div>
        <button
          type="button"
          className="pp-medcal-nav"
          aria-label="Next month"
          onClick={() => onMonthChange(new Date(year, month + 1, 1))}
        >
          ›
        </button>
      </div>

      <div className="pp-medcal-grid pp-medcal-dow">
        {DOW.map((d, i) => (
          <div key={`${d}-${i}`} className="pp-medcal-dow-cell">
            {d}
          </div>
        ))}
      </div>

      <div className="pp-medcal-grid">
        {cells.map((cell, i) => {
          if (!cell) return <div key={`pad-${i}`} className="pp-medcal-pad" />;
          const free = (slotsByDate || {})[cell.date] || [];
          const published = (statusByDate || {})[cell.date] || [];
          const past = minDate && cell.date < minDate;
          const bookable = !past && free.length > 0;
          // Published-but-full is a real state and must look different from a
          // day the clinic simply isn't open. It stays OPENABLE so the patient
          // can see the times struck through and understand why — disabling it
          // would make a full day indistinguishable from a closed one.
          const fullOnly = !past && free.length === 0 && published.length > 0;
          const openable = bookable || fullOnly;
          return (
            <button
              key={cell.date}
              type="button"
              className={[
                "pp-medcal-day",
                bookable ? "is-open" : "",
                cell.date === selectedDate ? "is-selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              disabled={!openable}
              aria-label={
                bookable
                  ? `${cell.date}, ${free.length} time${free.length === 1 ? "" : "s"} available`
                  : fullOnly
                    ? `${cell.date}, fully booked`
                    : `${cell.date}, no appointments`
              }
              style={{
                // Three tiers: bookable, open-but-full, and nothing at all.
                opacity: bookable ? 1 : fullOnly ? 0.6 : 0.3,
                cursor: openable ? "pointer" : "default",
                background: "none",
              }}
              onClick={() => openable && onSelectDate(cell.date)}
            >
              <span className="pp-medcal-num">{cell.day}</span>
              {bookable && (
                <span className="pp-medcal-flag" aria-hidden="true">
                  ●
                </span>
              )}
              {fullOnly && (
                <span
                  className="pp-medcal-flag"
                  aria-hidden="true"
                  style={{ opacity: 0.5 }}
                >
                  ×
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
